import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useAuth } from "../contexts/useAuth";
import { useTeamScope } from "../contexts/useTeamScope";

/*
 * Below this width the destinations move to a bar at the bottom edge: at the
 * board a phone is held in one hand, and the thumb reaches the bottom, not the top.
 * Above it they sit in the header, where a mouse expects them.
 */
const BOTTOM_BAR_UP_TO = "767px";

/*
 * The blur sits on a pseudo-element, not on the bar itself. `backdrop-filter` turns
 * an element into the containing block for every fixed descendant, which pinned the
 * bottom bar to the header instead of to the viewport - it rendered right under the
 * logo. A pseudo-element is a child, so it blurs without becoming an ancestor.
 */
const NavbarContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  min-height: var(--navbar-height);
  border-bottom: 1px solid var(--line);
  z-index: 1000;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(9, 17, 32, 0.82);
    backdrop-filter: blur(16px);
    z-index: -1;
  }
`;

const NavbarInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-4);
  width: min(100%, var(--content-width));
  min-height: var(--navbar-height);
  margin: 0 auto;
  padding: 0 var(--space-4);
`;

const Brand = styled.button`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-width: 0;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  color: inherit;
  text-align: left;
`;

const BrandText = styled.span`
  display: grid;
  min-width: 0;

  @media (max-width: 520px) {
    display: none;
  }
`;

const BrandTitle = styled.span`
  color: var(--text);
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const BrandSubtitle = styled.span`
  color: var(--text-faint);
  font-size: 0.72rem;
  letter-spacing: 0.02em;
`;

const Logo = styled.img`
  height: 2.5rem;
  width: auto;
`;

const HeaderCluster = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-width: 0;
`;

/*
 * One bar, two positions. On a phone it is fixed to the bottom edge and carries the
 * safe area of the home indicator; from tablet width it is simply part of the header.
 */
const Destinations = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-1);

  @media (max-width: ${BOTTOM_BAR_UP_TO}) {
    position: fixed;
    left: 0;
    bottom: 0;
    width: 100%;
    gap: 0;
    /* Opaque rather than blurred: a blur here would make this element the
       containing block of nothing, but it also costs a compositor layer for no
       gain - content scrolls behind an edge bar, it does not shine through it. */
    background: var(--navy-850);
    border-top: 1px solid var(--line);
    padding-bottom: env(safe-area-inset-bottom, 0);
    z-index: 1000;
  }
`;

const NavButton = styled.button<{ $active?: boolean }>`
  position: relative;
  border: none;
  background: none;
  color: ${({ $active }) => ($active ? "var(--text)" : "var(--text-muted)")};
  font: inherit;
  font-size: 0.9rem;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  cursor: pointer;
  margin: 0;
  min-height: 2.75rem;
  padding: 0 var(--space-3);
  border-radius: var(--radius-sm);
  white-space: nowrap;
  transition: color 140ms ease, background-color 140ms ease;

  &::after {
    content: "";
    position: absolute;
    left: var(--space-3);
    right: var(--space-3);
    bottom: -0.35rem;
    height: 2px;
    border-radius: var(--radius-pill);
    background: ${({ $active }) => ($active ? "var(--accent)" : "transparent")};
  }

  &:hover {
    color: var(--text);
  }

  @media (max-width: ${BOTTOM_BAR_UP_TO}) {
    flex: 1 1 0;
    /* 44px is the smallest tap target that is reliably hit; this is well above it. */
    min-height: 3.25rem;
    padding: 0 var(--space-1);
    font-size: 0.78rem;
    border-radius: 0;

    &::after {
      left: 0;
      right: 0;
      top: 0;
      bottom: auto;
    }
  }
`;

const TeamChip = styled.button`
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-pill);
  background: transparent;
  color: var(--text-muted);
  font: inherit;
  font-size: 0.78rem;
  min-height: 2rem;
  max-width: 11rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 var(--space-3);
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
`;

const AuthButton = styled.button`
  min-height: 2.5rem;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius);
  background: transparent;
  color: var(--text);
  font: inherit;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0 var(--space-4);
  white-space: nowrap;
  cursor: pointer;
  transition: border-color 140ms ease;

  &:hover {
    border-color: var(--accent-line);
  }
`;

/*
 * Five destinations, cut by occasion rather than by data model: what is coming up
 * (the calendar), what the numbers say, the training evening, the match day, and the
 * rest. Eight entries in a burger put every one of them two taps away and hid what the
 * app can do until it was open.
 */
const NAV_ITEMS: ReadonlyArray<{ path: string; label: string; captainOnly?: boolean }> = [
  // Fuer jeden: was ansteht und die eigene Zusage oder Absage (Teamkalender, AD-010 Nachtrag).
  { path: "/termine", label: "Termine" },
  { path: "/team", label: "Team" },
  { path: "/training", label: "Training" },
  { path: "/spieltag", label: "Spieltag" },
  // Saison, Kader und Kalender gehoeren dem Mannschaftsfuehrer. Fuer einen Spieler
  // waere der Eintrag eine Tuer, hinter der nur Absagen stehen.
  { path: "/verwaltung", label: "Verwaltung", captainOnly: true },
];

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isAuthBypassed, loading, login, logout } = useAuth();
  const { team, teams, setTeam, isCaptain, loading: scopeLoading } = useTeamScope();
  const destinations = NAV_ITEMS.filter((item) => !item.captainOnly || isCaptain);

  const isRouteActive = (path: string) => location.pathname.startsWith(path);

  // With a single team there is nothing to switch, so the chip only names it.
  const canSwitchTeam = teams.length > 1;

  function switchTeam() {
    if (!canSwitchTeam) return;
    const next = teams[(teams.indexOf(team) + 1) % teams.length];
    setTeam(next);
  }

  return (
    <NavbarContainer aria-label="Hauptnavigation">
      <NavbarInner>
        <Brand type="button" onClick={() => navigate("/")}>
          {/*
            Hier bleibt das Vereinswappen, nicht das DartHub-Logo: dessen Schriftzug ist auf
            2,5 rem Höhe unlesbar und stünde doppelt neben dem Titel rechts davon. Wappen für
            den Verein, Schrift für die App. Das DartHub-Logo steht auf der Startseite, wo es
            groß genug ist, um gelesen zu werden.
          */}
          <Logo src="/logo_tvc.png" alt="TV Cannstatt 1846 e.V." />
          <BrandText>
            <BrandTitle>TVC DartHub</BrandTitle>
            <BrandSubtitle>Aufstellung nach Leistung</BrandSubtitle>
          </BrandText>
        </Brand>

        <HeaderCluster>
          {isAuthenticated && (
            <Destinations>
              {destinations.map((item) => (
                <NavButton
                  key={item.path}
                  $active={isRouteActive(item.path)}
                  type="button"
                  onClick={() => navigate(item.path)}
                >
                  {item.label}
                </NavButton>
              ))}
            </Destinations>
          )}

          {/*
            Der Platz der Mannschaft bleibt besetzt, solange sie noch ermittelt wird. Vorher stand
            hier nichts, und zwischen Anmeldung und dem Erscheinen von "TVC Triplemakers II" lag
            der Kaltstart der Accounts-App - gemessen knapp zwei Sekunden (R-034). Wer in dieser
            Zeit auf eine leere Kopfzeile sieht, hat keinen Anhaltspunkt, ob etwas passiert.
          */}
          {isAuthenticated && !team && scopeLoading && (
            <TeamChip type="button" disabled aria-live="polite">
              Anmeldung läuft …
            </TeamChip>
          )}

          {isAuthenticated && team && (
            <TeamChip
              type="button"
              disabled={!canSwitchTeam}
              title={canSwitchTeam ? "Team wechseln" : team}
              aria-label={canSwitchTeam ? `Team wechseln, aktuell ${team}` : `Team ${team}`}
              onClick={switchTeam}
            >
              {team}
            </TeamChip>
          )}

          {!loading && !isAuthBypassed && (
            <AuthButton type="button" onClick={() => void (isAuthenticated ? logout() : login())}>
              {isAuthenticated ? "Logout" : "Login"}
            </AuthButton>
          )}
        </HeaderCluster>
      </NavbarInner>
    </NavbarContainer>
  );
}

export default Navbar;
